import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/joy';

interface ImageCaptureProps {
    title?: string;
    onCapture: (success: boolean) => void;
}

const ImageCapture: React.FC<ImageCaptureProps> = ({ title, onCapture }) => {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        startCamera();
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: false
            });

            setStream(stream);

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current?.play();
                    onCapture(true);
                    setIsLoading(false);
                };
            }
            setError('');
        } catch (err) {
            setError('Failed to start camera. Please check your camera permissions and try again.');
            console.error('Error starting camera:', err);
            onCapture(false);
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '640px', margin: '0 auto' }}>
            {title && (
                <Typography
                    level="title-lg"
                    sx={{ mb: 2, color: '#ea7a23', textAlign: 'center' }}
                >
                    {title}
                </Typography>
            )}

            {!isLoading && error && (
                <Alert
                    color="danger"
                    variant="soft"
                    sx={{ mb: 2 }}
                >
                    {error}
                </Alert>
            )}

            <Box sx={{
                width: '100%',
                aspectRatio: '16/9',
                position: 'relative',
                borderRadius: 'lg',
                overflow: 'hidden',
                border: '2px solid',
                borderColor: '#ea7a23',
                backgroundColor: 'black'
            }}>
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                        width: '100%',
                        height: '100%',
                        transform: 'scaleX(-1)',
                        objectFit: 'cover'
                    }}
                />
                {isLoading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 1,
                        gap: 2
                    }}>
                        <CircularProgress size="lg" sx={{ color: '#ea7a23' }} />
                        <Typography level="body-sm" sx={{ color: 'white' }}>
                            Initializing camera...
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ImageCapture;